import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { message } from 'antd';
import {
  LaudoWizardData,
  LaudoStep,
  LAUDO_STEPS_CONFIG,
  ChecklistData,
  IdentificacaoData,
  VistoriaData,
  AvaliacaoData,
  AmostrasData,
  SignatureData,
  TransmissaoData,
  WizardMode,
} from '../../types/laudo-wizard.types';
import { InspectionRequest } from '@/services/types/inspection.types';
import { evaluatorSignatureService } from '@/services/evaluatorSignature.service';
import { responseService } from '@/services/checklists/response.service';
import { checklistService } from '@/services/checklists/checklist.service';
import { docmanService } from '@/services/docman.service';
import { blobToBase64 } from '../../pdf/utils/imageUtils';
import { parseLiquidez } from '../../utils/liquidez';

interface UseLaudoWizardProps {
  inspection: InspectionRequest;
  initialData?: LaudoWizardData;
  hasCompletedVideoCall?: boolean;
  mode?: WizardMode;
  /** Modo apenas checklist - para inspetor preencher checklist e fechar (sem gerar laudo) */
  checklistOnlyMode?: boolean;
  /** Dados de rascunho carregados do IndexedDB */
  draftData?: { data: LaudoWizardData; step: LaudoStep };
  /** Indica se o laudo sendo revisado é AVM (limita steps em modo review) */
  isAutomaticReport?: boolean;
  /** UserId do criador do report — usado para AVM carregar a assinatura do banco original em vez da do usuário logado */
  reportCreatedBy?: string;
}

export function useLaudoWizard({ inspection, initialData, hasCompletedVideoCall = false, mode = 'create', checklistOnlyMode = false, draftData, isAutomaticReport = false, reportCreatedBy }: UseLaudoWizardProps) {
  // Estado do step atual - começa no CHECKLIST se tiver video call ou modo checklist-only, senão IDENTIFICACAO
  const [currentStep, setCurrentStep] = useState<LaudoStep>(() => {
    // Se tem draft, restaurar o step salvo
    if (draftData && !initialData) {
      return draftData.step;
    }
    // No modo checklist-only, sempre começar no CHECKLIST
    if (checklistOnlyMode) {
      return LaudoStep.CHECKLIST;
    }
    // Se tem initialData, pode estar editando, então começar na IDENTIFICACAO
    if (initialData) {
      return LaudoStep.IDENTIFICACAO;
    }
    // Se tem video call completada, começar no CHECKLIST
    return hasCompletedVideoCall ? LaudoStep.CHECKLIST : LaudoStep.IDENTIFICACAO;
  });

  const [checklistCompleted, setChecklistCompleted] = useState(false);
  const checklistChecked = useRef(false);

  const draftNotified = useRef(false);

  // Estado dos dados do laudo (inicializado com dados da inspeção, draft ou dados iniciais fornecidos)
  const [laudoData, setLaudoData] = useState<LaudoWizardData>(() => {
    // Se tem draft, SEMPRE usar dados do rascunho (mesmo quando initialData existe)
    // O rascunho é mais recente que o initialData pois foi salvo explicitamente pelo usuário
    if (draftData) {
      const defaultData = initializeLaudoData(inspection);
      // Usar initialData como base se disponível, senão usar dados padrão
      const baseData = initialData || defaultData;
      // Mesclar draft sobre a base para garantir estrutura completa
      const mergedDraft: LaudoWizardData = {
        ...baseData,
        ...draftData.data,
        // Garantir que transmissao tenha a estrutura completa (draft nao salva processedLaudo)
        transmissao: {
          ...defaultData.transmissao,
          ...(draftData.data.transmissao || {}),
          transmissao: {
            ...defaultData.transmissao.transmissao,
            ...(draftData.data.transmissao?.transmissao || {}),
          },
          pdfGerado: false,
          processedLaudo: undefined,
          logoBase64: undefined,
        },
      };

      // Se existe memorial de calculo preliminar e o draft nao tem amostras preenchidas
      const preliminaryRaw = inspection.items?.[0]?.preliminaryCalculationsData;
      if (preliminaryRaw && mergedDraft.amostras?.skipCalculations) {
        try {
          const preliminaryAmostras = JSON.parse(preliminaryRaw) as AmostrasData;
          mergedDraft.amostras = { ...mergedDraft.amostras, ...preliminaryAmostras, skipCalculations: false };
        } catch {
          // JSON invalido - ignora
        }
      }

      return mergedDraft;
    }

    if (!initialData) {
      const data = initializeLaudoData(inspection);

      // Se existe memorial de cálculo preliminar salvo, pré-carregar nas amostras
      const preliminaryRaw = inspection.items?.[0]?.preliminaryCalculationsData;
      if (preliminaryRaw) {
        try {
          const preliminaryAmostras = JSON.parse(preliminaryRaw) as AmostrasData;
          data.amostras = { ...data.amostras, ...preliminaryAmostras };
        } catch {
          // JSON inválido - ignora
        }
      }

      return data;
    }

    // Se initialData foi fornecido, mesclar com valores padrão para garantir estrutura completa
    const defaultData = initializeLaudoData(inspection);
    const mergedData = {
      ...initialData,
      // Garantir que transmissao sempre tenha a estrutura completa
      transmissao: {
        ...defaultData.transmissao,
        ...(initialData.transmissao || {}),
        transmissao: {
          ...defaultData.transmissao.transmissao,
          ...(initialData.transmissao?.transmissao || {}),
        },
      },
    };

    const hasNoCalculations = initialData.amostras?.skipCalculations ||
      (!initialData.amostras?.dadosDepreciacao?.length && !initialData.amostras?.dadosComparativoDireto?.length);
    if (hasNoCalculations) {
      const preliminaryRaw = inspection.items?.[0]?.preliminaryCalculationsData;
      if (preliminaryRaw) {
        try {
          const preliminaryAmostras = JSON.parse(preliminaryRaw) as AmostrasData;
          mergedData.amostras = { ...mergedData.amostras, ...preliminaryAmostras, skipCalculations: false };
        } catch {
          // JSON inválido - ignora
        }
      }
    }

    return mergedData;
  });

  const signatureLoaded = useRef(false);
  const fotoFachadaLoaded = useRef(false);
  const preliminaryNotified = useRef(false);

  // Notificar que dados do rascunho ou preliminares foram carregados
  useEffect(() => {
    if (draftData && !initialData && !draftNotified.current) {
      draftNotified.current = true;
      message.info('Rascunho carregado. Continuando de onde parou.');
      return;
    }
    if (!initialData && !preliminaryNotified.current) {
      const preliminaryRaw = inspection.items?.[0]?.preliminaryCalculationsData;
      if (preliminaryRaw) {
        preliminaryNotified.current = true;
        message.info('Dados preliminares de cálculo carregados');
      }
    }
  }, []);

  // Carregar foto de fachada como base64 via backend quando estiver editando/corrigindo
  // Usa downloadFile (proxy backend) em vez de presigned URL pra evitar falhas de fetch
  // direto no S3 que faziam a foto sumir do PDF após ciclos de correção.
  useEffect(() => {
    const fotoFachadaDocumentId = laudoData.vistoria?.fotoFachadaDocumentId;
    const fotoFachada = laudoData.vistoria?.fotoFachada;

    if (fotoFachadaDocumentId && !fotoFachada && !fotoFachadaLoaded.current) {
      fotoFachadaLoaded.current = true;

      docmanService
        .downloadFile(fotoFachadaDocumentId)
        .then(blobToBase64)
        .then((base64) => {
          setLaudoData((prev) => {
            // Usuário pode ter anexado outra foto (ou removido) enquanto o
            // download rodava — aplicar aqui reverteria a troca para a foto antiga.
            if (
              prev.vistoria?.fotoFachadaDocumentId !== fotoFachadaDocumentId ||
              prev.vistoria?.fotoFachada
            ) {
              return prev;
            }
            return {
              ...prev,
              vistoria: {
                ...prev.vistoria,
                fotoFachada: base64,
              },
              transmissao: {
                ...prev.transmissao,
                pdfGerado: false,
                processedLaudo: undefined,
              },
            };
          });
        })
        .catch((err) => {
          console.error('[fotoFachada] Falha ao carregar via backend:', err);
          fotoFachadaLoaded.current = false;
        });
    }
  }, [laudoData.vistoria?.fotoFachadaDocumentId, laudoData.vistoria?.fotoFachada]);

  useEffect(() => {
    // No modo checklist-only, não pular automaticamente (inspetor precisa preencher)
    if (checklistOnlyMode || !hasCompletedVideoCall || checklistChecked.current) {
      return;
    }

    const checkExistingChecklistResponse = async () => {
      const firstItem = inspection.items?.[0];
      const inspectionTypeId = firstItem?.inspectionTypeId;
      const inspectionId = inspection.id;

      if (!inspectionTypeId || !inspectionId) {
        return;
      }

      try {
        checklistChecked.current = true;

        const checklistResponse = await checklistService.getChecklistByInspectionType(
          inspectionTypeId,
          1,
          1
        );

        if (!checklistResponse?.items?.[0]) {
          return;
        }

        const checklistId = checklistResponse.items[0].id;

        const responses = await responseService.getResponsesByInspectionId(checklistId, inspectionId);

        if (responses && Array.isArray(responses) && responses.length > 0) {
          setChecklistCompleted(true);
          setCurrentStep(LaudoStep.IDENTIFICACAO);
          setLaudoData((prev) => ({
            ...prev,
            checklist: {
              ...prev.checklist,
              checklistPreenchido: true,
              checklistId: checklistId,
            },
          }));
        }
      } catch {
        // Ignore error
      }
    };

    checkExistingChecklistResponse();
  }, [inspection.id, inspection.items, hasCompletedVideoCall, checklistOnlyMode]);

  useEffect(() => {
    if (currentStep === LaudoStep.AMOSTRAS && !signatureLoaded.current) {
      signatureLoaded.current = true;

      const signaturePromise = isAutomaticReport && reportCreatedBy
        ? evaluatorSignatureService.getSignatureByUserId(reportCreatedBy)
        : evaluatorSignatureService.getSignature();

      signaturePromise
        .then((savedData) => {
          if (savedData) {
            setLaudoData((prev) => ({
              ...prev,
              signature: {
                evaluatorName: savedData.evaluatorName,
                creaCauNumber: savedData.creaCauNumber || '',
                signatureImage: savedData.signatureBase64,
                _saved: true, // Marca como salvo pois foi carregado do servidor
              },
            }));
          }
        })
        .catch(() => {
          // Ignore error
        });
    }
  }, [currentStep, isAutomaticReport, reportCreatedBy]);

  const getNextStepEditAutomatic = (current: LaudoStep): LaudoStep | null => {
    switch (current) {
      case LaudoStep.IDENTIFICACAO: return LaudoStep.VISTORIA;
      case LaudoStep.VISTORIA: return LaudoStep.AVALIACAO;
      case LaudoStep.AVALIACAO: return LaudoStep.AMOSTRAS;
      case LaudoStep.AMOSTRAS: return LaudoStep.ASSINATURA;
      case LaudoStep.ASSINATURA: return LaudoStep.TRANSMISSAO;
      default: return null;
    }
  };

  const getPrevStepEditAutomatic = (current: LaudoStep): LaudoStep | null => {
    switch (current) {
      case LaudoStep.TRANSMISSAO: return LaudoStep.ASSINATURA;
      case LaudoStep.ASSINATURA: return LaudoStep.AMOSTRAS;
      case LaudoStep.AMOSTRAS: return LaudoStep.AVALIACAO;
      case LaudoStep.AVALIACAO: return LaudoStep.VISTORIA;
      case LaudoStep.VISTORIA: return LaudoStep.IDENTIFICACAO;
      default: return null;
    }
  };

  // Modo calculations: steps pré-Amostras são read-only, Amostras/Assinatura/Transmissão editáveis
  const getNextStepCalculations = (current: LaudoStep): LaudoStep | null => {
    switch (current) {
      case LaudoStep.IDENTIFICACAO: return LaudoStep.VISTORIA;
      case LaudoStep.VISTORIA: return LaudoStep.AVALIACAO;
      case LaudoStep.AVALIACAO: return LaudoStep.AMOSTRAS;
      case LaudoStep.AMOSTRAS: return LaudoStep.ASSINATURA;
      case LaudoStep.ASSINATURA: return LaudoStep.TRANSMISSAO;
      default: return null;
    }
  };

  const getPrevStepCalculations = (current: LaudoStep): LaudoStep | null => {
    switch (current) {
      case LaudoStep.TRANSMISSAO: return LaudoStep.ASSINATURA;
      case LaudoStep.ASSINATURA: return LaudoStep.AMOSTRAS;
      case LaudoStep.AMOSTRAS: return LaudoStep.AVALIACAO;
      case LaudoStep.AVALIACAO: return LaudoStep.VISTORIA;
      case LaudoStep.VISTORIA: return LaudoStep.IDENTIFICACAO;
      default: return null;
    }
  };

  // Função para pular cálculos (prestadora envia sem amostras)
  const skipCalculations = useCallback(() => {
    setLaudoData((prev) => ({
      ...prev,
      amostras: {
        ...prev.amostras,
        skipCalculations: true,
      },
    }));
    setCurrentStep(LaudoStep.ASSINATURA);
  }, []);

  const goToNext = useCallback(() => {
    // Validação de amostras mínimas (não aplicar quando skipCalculations está ativo)
    if (currentStep === LaudoStep.AMOSTRAS && !laudoData.amostras.skipCalculations) {
      const numAmostras = laudoData.amostras.dadosDepreciacao.filter(
        (a) => a.identificacao !== 'Avaliando'
      ).length;

      if (numAmostras < 5) {
        message.warning({
          content: `Número insuficiente de amostras. Apenas ${numAmostras} amostra(s). Mínimo necessário: 5 amostras.`,
          duration: 5,
        });
        return;
      }
    }

    if (mode === 'editAutomatic' || (mode === 'review' && isAutomaticReport)) {
      const nextStep = getNextStepEditAutomatic(currentStep);
      if (nextStep !== null) {
        setCurrentStep(nextStep);
      }
      return;
    }

    if (mode === 'calculations') {
      const nextStep = getNextStepCalculations(currentStep);
      if (nextStep !== null) {
        setCurrentStep(nextStep);
      }
      return;
    }

    if (currentStep < LaudoStep.TRANSMISSAO) {
      setCurrentStep((prev) => {
        const nextStep = prev + 1;
        // Em modo review, pular o step de ASSINATURA (de AMOSTRAS vai direto para TRANSMISSAO)
        if (mode === 'review' && nextStep === LaudoStep.ASSINATURA) {
          return LaudoStep.TRANSMISSAO;
        }
        return nextStep;
      });
    }
  }, [currentStep, laudoData.amostras.dadosDepreciacao, laudoData.amostras.skipCalculations, mode, isAutomaticReport]);

  // Função para invalidar o PDF gerado (forçar regeneração)
  const invalidatePdf = useCallback(() => {
    setLaudoData((prev) => ({
      ...prev,
      transmissao: {
        ...prev.transmissao,
        pdfGerado: false,
        processedLaudo: undefined,
      },
    }));
  }, []);

  const goToPrevious = useCallback(() => {
    if (mode === 'editAutomatic' || (mode === 'review' && isAutomaticReport)) {
      const prevStep = getPrevStepEditAutomatic(currentStep);
      if (prevStep !== null) {
        invalidatePdf();
        setCurrentStep(prevStep);
      }
      return;
    }

    if (mode === 'calculations') {
      const prevStep = getPrevStepCalculations(currentStep);
      if (prevStep !== null) {
        invalidatePdf();
        setCurrentStep(prevStep);
      }
      return;
    }

    const minStep = hasCompletedVideoCall ? LaudoStep.CHECKLIST : LaudoStep.IDENTIFICACAO;
    if (currentStep > minStep) {
      // Invalidar o PDF quando voltar (usuário pode ter alterado dados)
      invalidatePdf();
      setCurrentStep((prev) => {
        const prevStep = prev - 1;
        // Em modo review, pular o step de ASSINATURA (de TRANSMISSAO volta direto para AMOSTRAS)
        if (mode === 'review' && prevStep === LaudoStep.ASSINATURA) {
          return LaudoStep.AMOSTRAS;
        }
        return prevStep;
      });
    }
  }, [currentStep, hasCompletedVideoCall, invalidatePdf, mode, isAutomaticReport]);

  const goToStep = useCallback((step: LaudoStep) => {
    // Se estiver voltando para um step anterior, invalidar o PDF
    if (step < currentStep) {
      invalidatePdf();
    }
    setCurrentStep(step);
  }, [currentStep, invalidatePdf]);

  const updateChecklist = useCallback((data: Partial<ChecklistData>) => {
    setLaudoData((prev) => ({
      ...prev,
      checklist: { ...prev.checklist, ...data },
    }));

    // Se o checklist foi preenchido, avançar automaticamente (exceto no modo checklist-only)
    // No modo checklist-only, o onChecklistCompleted do useStepRenderer fecha o wizard
    if (data.checklistPreenchido && !checklistOnlyMode) {
      setChecklistCompleted(true);
      setTimeout(() => {
        setCurrentStep(LaudoStep.IDENTIFICACAO);
      }, 1500);
    }
  }, [checklistOnlyMode]);

  const updateIdentificacao = useCallback((data: Partial<IdentificacaoData>) => {
    setLaudoData((prev) => {
      const newState = {
        ...prev,
        identificacao: { ...prev.identificacao, ...data },
      };

      if (data.tipoImovel && data.tipoImovel !== prev.identificacao.tipoImovel) {
        newState.avaliacao = {
          ...prev.avaliacao,
          dadosImovel: {
            ...prev.avaliacao.dadosImovel,
            tipoImovel: data.tipoImovel,
          },
        };
      }

      return newState;
    });
  }, []);

  const updateVistoria = useCallback((data: Partial<VistoriaData>) => {
    setLaudoData((prev) => ({
      ...prev,
      vistoria: { ...prev.vistoria, ...data },
    }));
  }, []);

  const updateAvaliacao = useCallback((data: Partial<AvaliacaoData>) => {
    setLaudoData((prev) => {
      // DEEP MERGE para preservar dados aninhados
      const newAvaliacao: AvaliacaoData = {
        dadosImovel: {
          ...prev.avaliacao.dadosImovel,
          ...(data.dadosImovel || {}),
        },
        dadosTerreno: {
          ...prev.avaliacao.dadosTerreno,
          ...(data.dadosTerreno || {}),
        },
        dadosCondominio: {
          ...prev.avaliacao.dadosCondominio,
          ...(data.dadosCondominio || {}),
        },
        _initialized: data._initialized ?? prev.avaliacao._initialized,
      };
      return {
        ...prev,
        avaliacao: newAvaliacao,
      };
    });
  }, []);

  const updateAmostras = useCallback((data: Partial<AmostrasData>) => {
    setLaudoData((prev) => ({
      ...prev,
      amostras: { ...prev.amostras, ...data },
    }));
  }, []);

  const updateSignature = useCallback((data: Partial<SignatureData>) => {
    setLaudoData((prev) => ({
      ...prev,
      signature: { ...prev.signature, ...data },
    }));
  }, []);

  const updateTransmissao = useCallback((data: Partial<TransmissaoData>) => {
    setLaudoData((prev) => ({
      ...prev,
      transmissao: { ...prev.transmissao, ...data },
    }));
  }, []);

  // Validação do step atual
  const canGoNext = useMemo(() => {
    switch (currentStep) {
      case LaudoStep.CHECKLIST:
        return true; // Permite avançar mesmo sem preencher

      case LaudoStep.IDENTIFICACAO:
      case LaudoStep.VISTORIA:
        return true; // Readonly, sempre pode avançar

      case LaudoStep.AVALIACAO: {
        const { dadosImovel, dadosTerreno } = laudoData.avaliacao;
        const isTerrenoType = dadosImovel.tipoImovel === 'Terreno';
        return !!(
          dadosImovel.tipoImovel &&
          dadosImovel.tipoImplantacao &&
          (isTerrenoType || dadosImovel.areaConstruida >= 0) &&
          (isTerrenoType || dadosImovel.estadoConservacao) &&
          (isTerrenoType || dadosImovel.padraoAcabamento) &&
          parseLiquidez(dadosImovel.fatorLiquidez) &&
          dadosImovel.idadeAparente >= 0 &&
          dadosTerreno.padraoTerreno &&
          dadosTerreno.topografia
        );
      }

      case LaudoStep.AMOSTRAS:
        return true; // Permite avançar sem exigir mínimo de amostras

      case LaudoStep.ASSINATURA:
        // Permite avançar apenas se:
        // 1. Os campos obrigatórios estiverem preenchidos
        // 2. O usuário clicou em "Salvar" (_saved = true)
        return !!(
          laudoData.signature.evaluatorName &&
          laudoData.signature.signatureImage &&
          laudoData.signature._saved
        );

      case LaudoStep.TRANSMISSAO:
        return false; // Última etapa

      default:
        return true;
    }
  }, [currentStep, laudoData]);

  const minStep = hasCompletedVideoCall ? LaudoStep.CHECKLIST : LaudoStep.IDENTIFICACAO;
  const canGoPrevious = mode === 'editAutomatic'
    ? currentStep !== LaudoStep.IDENTIFICACAO
    : mode === 'calculations'
      ? currentStep !== LaudoStep.IDENTIFICACAO
      : currentStep > minStep;

  // Progresso do wizard
  const progress = useMemo(() => {
    return ((currentStep + 1) / LAUDO_STEPS_CONFIG.length) * 100;
  }, [currentStep]);

  return {
    // Estado
    currentStep,
    laudoData,
    stepsConfig: LAUDO_STEPS_CONFIG,
    signatureDataLoaded: signatureLoaded.current,

    // Navegação
    goToNext,
    goToPrevious,
    goToStep,
    canGoNext,
    canGoPrevious,
    progress,

    // Ações especiais
    skipCalculations,

    // Atualização de dados
    updateChecklist,
    updateIdentificacao,
    updateVistoria,
    updateAvaliacao,
    updateAmostras,
    updateSignature,
    updateTransmissao,
  };
}

// ============================================
// FUNÇÕES AUXILIARES
// ============================================

function initializeLaudoData(inspection: InspectionRequest): LaudoWizardData {
  const firstItem = inspection.items?.[0];

  return {
    checklist: {
      checklistId: '',
      checklistPreenchido: false,
      respostas: [],
    },

    identificacao: {
      proponente: 'N/A', // TODO: Buscar do Salesforce
      enderecoCompleto: `${firstItem?.street || ''}, ${firstItem?.number || ''}, ${firstItem?.district || ''}, ${firstItem?.city || ''}/${firstItem?.state || ''}`,
      matriculaNumero: firstItem?.registrationNumber || '',
      matriculaCartorio: firstItem?.registryOfficeNumber || '',
      areaTotal: firstItem?.landArea || 0,
      areaPrivativa: firstItem?.privateArea || 0,
      tipoSolicitacao: 'Avaliação de Imóvel',
      tipoImovel: firstItem?.propertyTypeName || '',
    },

    vistoria: {
      checklistId: '', // TODO: Buscar checklist associado
      checklistRespostas: {},
      dataVistoria: new Date().toISOString(),
      vistoriador: 'N/A', // TODO: Buscar do sistema
    },

    avaliacao: {
      dadosImovel: {
        tipoImovel: firstItem?.propertyTypeName || '',
        tipoImplantacao: '',
        areaConstruida: 0,
        // Valores string para compatibilidade com Select do barihaus
        quantidadeQuartos: '0' as any,
        quantidadeBanheiros: '0' as any,
        quantidadeVagas: '0' as any,
        idadeAparente: 0,
        estadoConservacao: '',
        padraoAcabamento: '',
      },
      dadosTerreno: {
        areaTerreno: firstItem?.landArea || 0,
        testada: 0,
        padraoTerreno: '',
        topografia: '',
      },
      dadosCondominio: {
        areaComum: 0,
        estadoConservacao: '',
      },
    },

    amostras: {
      amostras: [],
      metodologiaImportacao: 'manual',
      dadosAreaConstruida: [],
      dadosDepreciacao: [],
      dadosValorTerreno: [],
      dadosComparativoDireto: [],
      switchesTerreno: {
        area: false,
        testada: false,
        vegetacao: false,
        fatorExtraI: false,
        fatorExtraII: false,
        fatorExtraIII: false,
      },
      comentarios: '',
    },

    signature: {
      evaluatorName: '',
      creaCauNumber: '',
      signatureImage: '',
    },

    transmissao: {
      transmissao: {
        sistemaDestino: 'Salesforce',
        statusTransmissao: 'pendente',
      },
      pdfGerado: false,
    },
  };
}

function validateAvaliacao(avaliacao: AvaliacaoData): boolean {
  const { dadosImovel, dadosTerreno } = avaliacao;

  // Helper para verificar se número é válido (aceita number ou string numérica)
  const isValidNumber = (val: any): boolean => {
    if (typeof val === 'number') return !isNaN(val);
    if (typeof val === 'string') return val.trim() !== '' && !isNaN(Number(val));
    return false;
  };

  // Helper para converter para número
  const toNumber = (val: any): number => {
    if (typeof val === 'number') return val;
    if (typeof val === 'string') return Number(val) || 0;
    return 0;
  };

  // Helper para verificar se string é válida (não vazia)
  const isValidString = (val: any): val is string =>
    typeof val === 'string' && val.trim().length > 0;

  // Validação alinhada com os campos obrigatórios do form (AvaliacaoStep)
  const isTerrenoType = dadosImovel?.tipoImovel === 'Terreno';
  const isValid = !!(
    // Dados do Imóvel - campos obrigatórios
    isValidString(dadosImovel?.tipoImovel) &&
    isValidString(dadosImovel?.tipoImplantacao) &&
    isValidNumber(dadosImovel?.areaConstruida) && toNumber(dadosImovel?.areaConstruida) >= 0 && // Permite 0 (ex: terreno)
    isValidNumber(dadosImovel?.quantidadeQuartos) &&   // Permite 0 (ex: kitnet)
    isValidNumber(dadosImovel?.quantidadeBanheiros) && // Permite 0
    isValidNumber(dadosImovel?.quantidadeVagas) &&     // Permite 0
    isValidNumber(dadosImovel?.idadeAparente) &&
    (isTerrenoType || isValidString(dadosImovel?.estadoConservacao)) &&
    (isTerrenoType || isValidString(dadosImovel?.padraoAcabamento)) &&
    parseLiquidez(dadosImovel?.fatorLiquidez) !== undefined &&
    // Dados do Terreno - campos obrigatórios
    isValidString(dadosTerreno?.padraoTerreno) &&
    isValidString(dadosTerreno?.topografia)
    // Dados do Condomínio - NÃO são obrigatórios
  );

  return isValid;
}
